import { interactiveSitePlanSvg } from "constants/svg-assets";
import MarketingStatus from "enums/marketingStatus";
import createPictureImageSources from "utils/createPictureImageSources";

export default class PaidSpecCard {
/**
 * Generates and sets the big image sources for different breakpoints.
 * Creates picture element sources with specified dimensions and parameters for responsive images.
 * Falls back to errorImage if thumbnail is not available.
 * 
 */
  getBigImageSources() {
    const { sources, src } = createPictureImageSources({
      url: this.thumbnail || this.errorImage,
      breakpoints: {
        '1.5x': {
          width: 447,
          height: 297
        },
        '2x': {
          width: 400,
          height: 312
        },
        '3x': {
          width: 900,
          height: 600
        }
      },
      irsParameters: {
        mode: 'crop',
        progressive: true
      }
    });

    this.bigSources = sources;
    this.src = src;
  }

  /**
   * Generates and sets the small image sources for different breakpoints.
   * Creates picture element sources with specified dimensions and parameters for responsive images.
   * Falls back to errorImage if thumbnail is not available.
   * 
   */
  getSmallImageSources() {
    const { sources, src } = createPictureImageSources({
      url: this.thumbnail || this.errorImage,
      breakpoints: {
        '230w': {
          maxwidth: 230,
          maxheight: 200
        },
        '434w': {
          maxwidth: 434,
          maxheight: 334
        },
        '541w': {
          maxwidth: 541,
          maxheight: 441
        },
        '632w': {
          maxwidth: 632,
          maxheight: 532
        },
        '716w': {
          maxwidth: 716,
          maxheight: 614
        }
      },
      irsParameters: {
        progressive: true
      }
    });

    this.smallSources = sources;
    this.src = src;
  }

  /**
   * Sets the metadata for the paid spec card component.
   * Initializes card properties with provided metadata values and triggers image source generation.
   */
  setMetadata(metadata) {
    this.thumbnail = metadata?.thumbnail;
    this.communityName = metadata?.communityName;
    this.priceText = metadata?.priceText;
    this.bedrooms = metadata?.bedrooms;
    this.bathrooms = metadata?.bathrooms;
    this.address = metadata?.address;
    this.brandName = metadata?.brandName;
    this.squareFeet = metadata?.squareFeet;
    this.id = metadata?.id;
    this.url = metadata?.url;
    this.isFeaturedListing = metadata?.isFeaturedListing;
    this.planTypeName = metadata?.planTypeName;
    this.city = metadata?.city;
    this.stateAbbreviation = metadata?.stateAbbreviation;
    this.zip = metadata?.zip;
    this.garages = metadata?.garages;
    this.name = metadata?.name;
    this.communityId = metadata?.communityId;
    this.sitePlanCenterLatitude = metadata?.sitePlanCenterLatitude;
    this.sitePlanCenterLongitude = metadata?.sitePlanCenterLongitude;
    this.communityStatus = metadata?.communityStatus;
    this.averageRating = metadata?.averageRating;
    this.previousPrice = metadata?.previousPrice;
    this.errorImage = null;

    this.getBigImageSources();
    this.getSmallImageSources();
  }

  /**
   * Renders a status label for a card based on priority conditions.
   * 
   * @returns {string} HTML string containing the status label div, or empty string if no conditions match
   */
  renderStatusLabel() {
    const statusConfig = [
      {
        condition: this.isFeaturedListing,
        class: "spotlight",
        label: "Spotlight",
        qa: "spotlight"
      },
      {
        condition: this.hasSelfGuidedTour,
        class: "self-tour",
        label: "Self-tour",
        qa: "self-tour"
      }
    ];
  
    const match = statusConfig.find(s => s.condition);
    if (!match) {
      return "";
    }
  
    return `
      <div class="nhs-u-absolute--top-left-300">
        <span class="nhs-c-listing-status nhs-c-listing-status--${match.class}" data-qa="${match.qa}">
          ${match.label}
        </span>
      </div>
    `;
  }

  /**
   * Renders the average rating display for a card component.
   * 
   * @returns {string} An HTML string containing a rating display with a star icon and rating value,
   *                   or an empty string if no average rating is available.
   *                   The returned HTML includes:
   *                   - A container div with ARIA label for accessibility
   *                   - An SVG star icon
   *                   - A span displaying the numeric rating value
   */
  renderRatingsAverage() {
    return this.averageRating
    ? `
      <div class="nhs-n1-c-card__rating" aria-label="${this.averageRating} out of 5 stars" data-qa="rating_card">
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" fill="none" viewBox="0 0 11 10">
          <path fill="#2a5c60" d="m5.25 0 1.24 3.82h4.01L7.255 6.18 8.495 10 5.25 7.64 2.005 10l1.24-3.82L0 3.82h4.01z"></path>
        </svg>
        <span>${this.averageRating}</span>
      </div>
    `
    : "";
  }

  /**
   * Renders a responsive picture element with multiple source formats for different screen sizes.
   */
  renderPicture() {
    return `
      <picture class="nhs-n1-c-card__image nhs-c-image--ratio-16-9">

        ${this.smallSources
          .map(
            source =>
              `<source media="(max-width: 767px)" srcset="${source.srcset}" type="${source.format}"></source>`
          )
          .join('')}

          
        ${this.bigSources
          .map(
            source =>
              `<source media="(min-width: 768px)" srcset="${source.srcset}" type="${source.format}"></source>`
          )
          .join('')}

        <img src="${this.src}"
            class="nhs-c-image--ratio-16-9"
            alt="${this.communityName} - ${this.address}"
            loading="eager"
            onerror="document.dispatchEvent(
              new CustomEvent('image-load-error', {
                bubbles: true,
                detail: { image: this }
              })
            )"
            onload="this.dataset.load=true">
      </picture>
    `;
  }

  /**
   * Returns the call to action based on the summary card properties.
   * @returns {string} The call to action text.
   */
  getCallToActionText() {
    if (this.communityStatus === MarketingStatus.ComingSoon.toString()) {
      return 'Get more information';
    }

    return 'Free brochure';
  }

  /**
   * Returns the lead generator opener based on the summary card information.
   * @returns {string} The lead generator opener.
   */
  getLeadGeneratorOpener() {
    if (this.communityStatus === MarketingStatus.ComingSoon.toString()) {
      return 'more-information-paid';
    }

    return 'get-brochure-paid';
  }

  /**
   * Gets the tracking label for the Spec card based on its current marketing status.
   * 
   * @returns {string} Returns 'HomeResults.ComingSoonMapCard.Spec' if the community status 
   * is 'ComingSoon', otherwise returns 'HomeResults.MapCard.Spec'.
   */
  getTrackingListingLabel() {
    if (this.communityStatus === MarketingStatus.ComingSoon.toString()) {
      return 'HomeResults.ComingSoonMapCard.Spec';
    }

    return 'HomeResults.MapCard.Spec';
  }

  /**
   * Generates the HTML markup for a card button based on whether an interactive site plan is available.
   * 
   * @returns {string} HTML string containing either an interactive map button with site plan data attributes
   *                   (including latitude, longitude, and community ID) or
   *                    a lead generator button with lead generation data attributes.
   */
  getCardButton() {
    if (!!this.sitePlanCenterLatitude && !!this.sitePlanCenterLongitude) {
      return `<button
            class="nhs-c-button--text-only nhs-c-button--primary-ui nhs-c-button--small-32 nhs-u-flex-row nhs-u-gap--200"
            data-interactive-plan-button
            data-latitude="${this.sitePlanCenterLatitude}"
            data-longitude="${this.sitePlanCenterLongitude}"
            data-community-id="${this.communityId}">
            <span class='nhs-c-icon--16'>${interactiveSitePlanSvg}</span> Interactive map
        </button>`;
    }

    return `
      <button
        class="nhs-c-button--primary-lead nhs-u-margin--top-auto nhs-c-button--small-32 nhs-c-button--text-only"
        data-button-action="${this.getCallToActionText()}"
        data-url="${this.url}"
        data-variation="paid"
        data-from-page="${this.getTrackingListingLabel()}"
        data-lead-generator-opener="${this.getLeadGeneratorOpener()}">
        ${this.getCallToActionText()}
      </button>
    `;
  }

/**
 * Generates the HTML markup for a paid spec card component.
 * 
 * The card displays property information including images, pricing, specifications (bedrooms, bathrooms),
 * location details, and interactive elements such as favorite buttons.
 */
  getHtml() {
    const classNames = ['nhs-n1-c-card--housing', 'nhs-n1-c-card--paidlisting', 'nhs-n1-c-card--surface'];
    if (this.isFeaturedListing) {
      classNames.push('nhs-n1-c-card--spotlight');
    }

    return `
      <div
        id="${this.id}"
        class="nhs-c-tooltip--map nhs-c-tooltip--card"
        data-standard-card
        data-card
        data-map-card
        data-id="${this.id}"
        data-type="spec"
        data-paid-spec
        data-is-basic="false"
        data-is-paid="true"
        data-spec-id="${this.id}"
        data-brand-name="${this.brandName}"
        data-community-name="${this.communityName}"
        data-community-id="${this.communityId}"
        data-marketing-status-label="${this.communityStatus}"
        ${this.isFeaturedListing ? `data-is-featured-listing = true` : ''}
        data-image-url="${this.thumbnail}"
        data-price="${this.priceText}"
        data-city="${this.city}"
        data-state-abbreviation="${this.stateAbbreviation}"
        data-zip="${this.zip}">
        <div class="${classNames.join(' ')}">
            <div class="nhs-n1-c-card__container">
                ${this.renderPicture()}
                ${this.renderStatusLabel()}
                <div class="nhs-n1-c-card__body" data-qa="card_body">
                  <div class="nhs-n1-c-card__content">
                    <h3 class="nhs-o-typography--souvenir-subhead-2-large-light nhs-u-text--2-line-ellipsis nhs-u-width--full">
                      <a href="${this.url}" target="_blank"
                        data-link
                        data-qa="spec-name">${this.name}
                      </a>
                    </h3>
                      <div class="nhs-u-flex-column nhs-u-gap--200 nhs-u-width--full">
                        <div class="nhs-u-flex-row nhs-u-gap--200">
                          <p class="nhs-n1-c-card__price" data-card-element="price">
                            <span data-qa="price_label"> ${this.priceText} </span>
                          </p>
                          ${this.previousPrice !== '' && this.previousPrice ? `
                          <div class="nhs-n1-c-card__badge nhs-n1-c-card__badge--reduced nhs-n1-c-card__badge--strikethrough" data-qa="strike-thru-price">
                            ${this.previousPrice}
                          </div>
                          ` : ''}
                        </div>
                        <ul class="nhs-n1-c-card__stats">
                          ${this.bedrooms ? `
                            <li class="nhs-n1-c-card__stats-stat">
                              <div class="nhs-n1-c-card__stats-stat-icon-box">
                                <svg class="nhs-n1-c-card__stats-stat-icon-svg" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                                <path fill="currentColor" fill-rule="evenodd"
                                    d="M9.5 3c-.597 0-1.134.263-1.5.678A1.995 1.995 0 0 0 6.5 3h-1a2 2 0 0 0-2 2v1H3a1.5 1.5 0 0 0-1.5 1.5v5a.5.5 0 0 0 1 0V10h11v2.5a.5.5 0 0 0 1 0v-5A1.5 1.5 0 0 0 13 6h-.5V5a2 2 0 0 0-2-2h-1Zm4 6V7.5A.5.5 0 0 0 13 7H3a.5.5 0 0 0-.5.5V9h11Zm-5-3V5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1h-3Zm-1-1v1h-3V5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1Z"
                                    clip-rule="evenodd"></path>
                                </svg>
                              </div>
                              <span>
                                <span class="nhs-n1-c-card__stats-stat-count">${this.bedrooms}</span>
                                ${this.bedrooms === '1' ? 'Bed' : 'Beds'}
                              </span>
                            </li>
                            ` : ''}
                            ${this.bathrooms ? `
                            <li class="nhs-n1-c-card__stats-stat">
                              <div class="nhs-n1-c-card__stats-stat-icon-box">
                                <svg class="nhs-n1-c-card__stats-stat-icon-svg" xmlns="http://www.w3.org/2000/svg" width="16"
                                height="16" fill="none" viewBox="0 0 16 16">
                                  <path fill="currentColor" fill-rule="evenodd"
                                      d="M5.25.5A2.75 2.75 0 0 0 2.5 3.25V8h-2a.5.5 0 0 0 0 1H2v2a2 2 0 0 0 2 2h.5v2h1v-2h5v2h1v-2h.5a2 2 0 0 0 2-2V9h1.5a.5.5 0 0 0 0-1h-12V3.25A1.75 1.75 0 0 1 6.982 3H5v1h5V3H7.989A2.75 2.75 0 0 0 5.25.5ZM3 11V9h10v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Zm3-6H5v1h1V5Zm1 0h1v1H7V5Zm3 0H9v1h1V5Z"
                                      clip-rule="evenodd">
                                  </path>
                                </svg>
                              </div>
                              <span>
                                <span class="nhs-n1-c-card__stats-stat-count">${this.bathrooms}</span>
                                ${this.bathrooms === '1' ? 'Bath' : 'Baths'}
                              </span>
                            </li>
                            ` : ''}
                            ${this.garages ? `
                            <li class="nhs-n1-c-card__stats-stat">
                              <div class="nhs-n1-c-card__stats-stat-icon-box">
                                <svg class="nhs-n1-c-card__stats-stat-icon-svg" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                                  <path fill="currentColor" fill-rule="evenodd" d="M9.33.625a.5.5 0 0 0-.66 0L2.527 6H2.5v.023l-1.83 1.6.66.753L2.5 7.353V15h1V6.478L9 1.665l5.5 4.813V15h1V7.353l1.17 1.024.66-.752-1.83-1.601V6h-.026L9.329.625ZM6 11.5V14h6v-2.684.185L6 11.5Zm0-.5v-.5h6V8H6v3Zm-.5-4h7a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-7a.5.5 0 0 1 .5-.5Z"
                                  clip-rule="evenodd"></path>
                                </svg>
                              </div>
                              <span>
                                <span class="nhs-n1-c-card__stats-stat-count">${this.garages}</span>
                                Gr
                              </span>
                            </li>
                            ` : ''}
                            ${this.squareFeet ? `
                              <li class="nhs-n1-c-card__stats-stat">
                                <div class="nhs-n1-c-card__stats-stat-icon-box">
                                  <svg class="nhs-n1-c-card__stats-stat-icon-svg" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                                    <path fill="currentColor" fill-rule="evenodd"
                                        d="M2.5 2a.5.5 0 0 1 .854-.353l11 11a.5.5 0 0 1-.354.854H3a.5.5 0 0 1-.5-.5V2Zm1 10.5h9.293L3.5 3.209V12.5Zm1.426-5a.5.5 0 0 1 .852-.354l3.209 3.18a.5.5 0 0 1-.352.855H5.426a.5.5 0 0 1-.5-.5v-3.18Zm1 2.681H7.42l-1.494-1.48v1.48Z"
                                        clip-rule="evenodd">
                                    </path>
                                  </svg>
                                </div>
                                <span>
                                  <span class="nhs-n1-c-card__stats-stat-count">${this.squareFeet}</span>
                                  <abbr title="Square feet">SqFt</abbr>
                                </span>
                              </li>
                            ` : ''}
                            <li class="nhs-n1-c-card__stats-stat nhs-u-width--full">
                              <div class="nhs-n1-c-card__stats-stat-icon-box">
                                <svg class="nhs-n1-c-card__stats-stat-icon-svg" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.66699 13.3408C4.66701 13.5419 4.72827 13.8513 4.89551 14.0947C5.04458 14.3116 5.28978 14.5 5.75 14.5C6.21022 14.5 6.45542 14.3116 6.60449 14.0947C6.77173 13.8513 6.83299 13.5419 6.83301 13.3408C6.83301 13.0648 7.05701 12.841 7.33301 12.8408H11.3887V9.5H12.3887V12.8408H15C15.2761 12.8408 15.5 13.0647 15.5 13.3408V13.3535C15.4999 13.3594 15.4993 13.367 15.499 13.376C15.4984 13.3941 15.4972 13.4188 15.4951 13.4482C15.4909 13.5069 15.4827 13.5875 15.4668 13.6826C15.4352 13.871 15.3713 14.1297 15.2383 14.3945C15.1042 14.6614 14.8955 14.9433 14.5732 15.1572C14.3123 15.3304 13.996 15.4444 13.6211 15.4834C13.5822 15.4931 13.5419 15.5 13.5 15.5H5.83301C5.82678 15.5 5.82063 15.4983 5.81445 15.498C5.79315 15.4986 5.77168 15.5 5.75 15.5C4.94372 15.5 4.39724 15.1354 4.07129 14.6611C3.76357 14.2133 3.66701 13.6928 3.66699 13.3408V9.5H4.66699V13.3408ZM7.52832 14.5C7.65116 14.2817 7.72885 14.0534 7.77539 13.8408H14.3906C14.3768 13.8757 14.3622 13.9115 14.3447 13.9463C14.2705 14.0941 14.1669 14.2271 14.0205 14.3242C13.877 14.4194 13.6626 14.5 13.333 14.5H7.52832Z" fill="#397E83"></path>
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M11.3887 2.65918C11.3886 1.98809 10.886 1.5 10.333 1.5H4.33887C4.54631 1.83752 4.66698 2.23612 4.66699 2.65918V4.82547H3.66699V3.15918H1C0.723858 3.15918 0.5 2.93532 0.5 2.65918C0.500041 1.48913 1.41079 0.500183 2.58301 0.5C2.60088 0.5 2.61897 0.502473 2.63672 0.50293C2.64667 0.50234 2.6569 0.5 2.66699 0.5H10.333C11.4981 0.5 12.3886 1.49758 12.3887 2.65918V4.7H11.3887V2.65918ZM1.60547 2.15918C1.7832 1.76217 2.16453 1.50014 2.58301 1.5C3.00153 1.5 3.38272 1.7622 3.56055 2.15918H1.60547Z" fill="#397E83"></path>
                                    <path d="M9.62073 2.54685C9.79639 2.46458 10.0039 2.49204 10.153 2.61619L15.153 6.78221C15.267 6.8772 15.3336 7.01858 15.3336 7.16697C15.3335 7.31524 15.2669 7.45583 15.153 7.55076L10.153 11.7178C10.0039 11.8417 9.79627 11.8683 9.62073 11.7861C9.44524 11.7037 9.33362 11.5269 9.33362 11.333V10.0859H2.66663C2.53414 10.0859 2.40685 10.034 2.31311 9.94041C2.21934 9.84664 2.16663 9.71853 2.16663 9.58592V4.83299C2.1668 4.55699 2.39059 4.33299 2.66663 4.33299H9.33362V2.99998L9.3385 2.92771C9.36251 2.76312 9.46728 2.61882 9.62073 2.54685ZM10.3336 4.83299C10.3336 5.10901 10.1096 5.33279 9.83362 5.33299H3.16663V9.08592H9.83362C10.1094 9.08611 10.3334 9.31011 10.3336 9.58592V10.2646L14.0514 7.166L10.3336 4.06736V4.83299Z" fill="#397E83"></path>
                                </svg>
                              </div>
                              <span class="nhs-u-text--ellipsis">Quick move-in ${this.planTypeName} in ${this.city}, ${this.stateAbbreviation} ${this.zip}</span>
                            </li>
                        </ul>
                        ${this.communityName ?`
                          <div class='nhs-n1-c-card__info nhs-u-text--ellipsis' data-qa="community-name">
                              ${this.communityName}
                          </div>
                        ` : ''}
                        ${this.brandName ? `
                          <div class="nhs-n1-c-card__info nhs-u-flex-row nhs-u-gap--200" data-qa="listing_brand">
                            <span class="nhs-u-text--ellipsis">By ${this.brandName}</span>
                            ${this.renderRatingsAverage()}
                          </div>
                        ` : ''}
                      </div>
                    </div>
                </div>
                ${this.getCardButton()}
            </div>
        </div>
      </div>
  `;
  }
}